import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  getCloudinaryUrl,
  Image,
  Modal,
  ModalDialogue,
  theme,
} from '@sg/garnish';

import { type IngredientModificationType } from '@order/utils';

import { INGREDIENT_IMAGE_WIDTH } from '../../constants';
import { useProductDetailsScreenContext } from '../../ProductDetailsScreen.provider';

// ────────────────────────────────────────────────────────────────────────────────

export const OutOfStockModal = (props: OutOfStockModalProps) => {
  const { setIngredientCategory } = props;

  const { formatMessage } = useIntl();
  const {
    restaurantName,
    product,
    isShowingOutOfStockWarning,
    outOfStockIngredients,
    closeOutOfStockWarning,
  } = useProductDetailsScreenContext();

  // ─────────────────────────────────────────────────────────────────────

  const handleCta = useCallback(() => {
    const firstIngredient = outOfStockIngredients[0];
    const category = firstIngredient.kind as IngredientModificationType;

    closeOutOfStockWarning();
    setIngredientCategory(category);
  }, [outOfStockIngredients, closeOutOfStockWarning, setIngredientCategory]);

  // ─────────────────────────────────────────────────────────────────────

  const isMissingMultipleIngredients = outOfStockIngredients.length > 1;

  const a11y = formatMessage(messages.outOfStockA11y);

  const body = formatMessage(
    isMissingMultipleIngredients
      ? messages.outOfStockBodyPlural
      : messages.outOfStockBodySingular,
    {
      product: product?.name,
      restaurant: restaurantName,
    },
  );

  const cta = formatMessage(
    isMissingMultipleIngredients
      ? messages.outOfStockCtaPlural
      : messages.outOfStockCtaSingular,
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      testID="pdp-modals.out-of-stock"
      size="medium"
      visible={isShowingOutOfStockWarning}
      accessibilityLabel={a11y}
    >
      <Modal.BodyText>
        <BodyText sizeMatch={['22']}>{body}</BodyText>
      </Modal.BodyText>

      <View style={styles.content}>
        {outOfStockIngredients.map((mod) => {
          const ingredientAssetUrl = mod.ingredient?.asset?.url;
          const ingredientImage = ingredientAssetUrl
            ? getCloudinaryUrl(ingredientAssetUrl, {
                crop: 'scale',
                width: INGREDIENT_IMAGE_WIDTH,
              })
            : null;

          return (
            <View key={mod.ingredient.name} style={styles.ingredient}>
              {ingredientImage ? (
                <Image source={ingredientImage} style={styles.image} />
              ) : null}

              <BodyText sizeMatch={['18']}>{mod.ingredient.name}</BodyText>
            </View>
          );
        })}
      </View>

      <Modal.Footer withoutTopBorder>
        <Button size="large" palette="primaryOutline" onPress={handleCta}>
          {cta}
        </Button>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: theme.spacing['6'],
    marginBottom: theme.spacing['6'],
    gap: theme.spacing['2'],
  },
  ingredient: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  image: {
    width: 30,
    height: 30,
    borderRadius: 30,
    backgroundColor: theme.colors.LIGHT,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  outOfStockA11y: {
    defaultMessage: 'Out of Stock',
    description: 'PDP | Out of Stock | A11y',
  },
  outOfStockBodySingular: {
    defaultMessage:
      'This item is usually in the {product}, but {restaurant} is currently out of stock. Thanks for understanding!',
    description: 'PDP | Out of Stock | Body (Singular)',
  },
  outOfStockBodyPlural: {
    defaultMessage:
      'These items are usually in the {product}, but {restaurant} is currently out of stock. Thanks for understanding!',
    description: 'PDP | Out of Stock | Body (Plural)',
  },
  outOfStockCtaSingular: {
    defaultMessage: 'Choose a new ingredient',
    description: 'PDP | Out of Stock | CTA (Singular)',
  },
  outOfStockCtaPlural: {
    defaultMessage: 'Choose new ingredients',
    description: 'PDP | Out of Stock | CTA (Plural)',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OutOfStockModalProps = {
  setIngredientCategory: (section: IngredientModificationType) => void;
};
